/**
 * Registered `lines.*` commands, their key bindings and the `#` palette provider (W2-07 lines-panel; DESIGN.md
 * §4.10, §5.5, §8.2, §8.3; WORKPACKAGES Appendix B). Each command has a menu, palette or button path; frequent
 * ones have keys in both presets:
 *   L line switcher · Shift+L add line · Ctrl+Up / Ctrl+Down previous / next line (export order) ·
 *   Ctrl+] / Ctrl+[ bring to front / send to back (no Mac chord: browser history) ·
 *   in the Lines panel: Delete / Backspace, Ctrl+C / Ctrl+X / Ctrl+V whole lines, Ctrl+A, Ctrl+D, F2 rename,
 *   H hide, Alt+Shift+Up / Down move in the export order (Alt-free: menus and palette).
 * RPE preset additions: Ctrl+wheel with nothing selected switches the line (LINE-01, on-screen label); Tab toggles
 * the preview's line info readout (VIEW-07; the readout and its command `preview.toggleReadout` are W2-02's).
 */

import type {
  AppServices,
  ArgSpec,
  CommandContext,
  CommandDef,
  KeyBinding,
  KeymapPresetId,
  PaletteItem,
  PaletteProvider,
} from '../../app/registry/types';
import type { LinePreset } from '../../core/commands/types';
import type { GroupId, LineId, UiElement } from '../../core/model/types';
import type { MessageRef } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import {
  activateLine,
  addLine,
  addRpeLines,
  changeTypeFlow,
  copyLines,
  createGroup,
  cutLines,
  deleteFlow,
  duplicateLines,
  lockedReason,
  moveLinesBy,
  pasteLines,
  selectLines,
  setGroup,
  setParentFlow,
  setZExtreme,
  showAll,
  stepActiveLine,
  targetLines,
  toggleAlsoShow,
  toggleFilter,
  togglePinnedLanes,
  unlockAll,
  wheelSwitch,
  type FilterSet,
} from './lineActions';
import { m } from './lines.messages';
import {
  buildLineTable,
  filterLines,
  filtersActive,
  matchLines,
  NO_FILTERS,
  previewDifferences,
  TYPE_ICONS,
  UI_ELEMENT_ICONS,
  UI_ELEMENT_IDS,
  type LineTable,
} from './lineModel';
import { linesUi, viewOf, VIEW_SETTING, type LinesModal } from './linesUi';
import { nowTracker } from './nowState';

type Ctx = CommandContext;

/** The panel id (dock tab). */
export const LINES_PANEL_ID = 'lines';

// ---------------------------------------------------------------------------------------------------------------
// Enablement
// ---------------------------------------------------------------------------------------------------------------

function needProject(ctx: Ctx): true | MessageRef {
  return ctx.store === null ? { key: m.noProject } : true;
}

function needLines(ctx: Ctx): true | MessageRef {
  const p = needProject(ctx);
  if (p !== true) return p;
  return ctx.store !== null && ctx.store.doc.lineOrder.length > 0 ? true : { key: m.noLines };
}

function needTargets(ctx: Ctx): true | MessageRef {
  const p = needProject(ctx);
  if (p !== true) return p;
  return targetLines(ctx).length > 0 ? true : { key: m.noLineSelected };
}

/** Targets that are not locked (structural edits). */
function needUnlockedTargets(ctx: Ctx): true | MessageRef {
  const t = needTargets(ctx);
  if (t !== true) return t;
  return lockedReason(ctx, targetLines(ctx)) ?? true;
}

function needSingleTarget(ctx: Ctx): true | MessageRef {
  const t = needUnlockedTargets(ctx);
  if (t !== true) return t;
  return targetLines(ctx).length === 1 ? true : { key: m.oneLineOnly };
}

// ---------------------------------------------------------------------------------------------------------------
// Argument readers (palette / keymap args are JSON)
// ---------------------------------------------------------------------------------------------------------------

function objArg(args: unknown): Readonly<Record<string, unknown>> {
  return typeof args === 'object' && args !== null ? (args as Readonly<Record<string, unknown>>) : {};
}

function boolArg(args: unknown, key: string, fallback: boolean): boolean {
  const v = objArg(args)[key];
  return typeof v === 'boolean' ? v : fallback;
}

function elementArg(args: unknown): UiElement | null {
  const v = typeof args === 'string' ? args : objArg(args).element;
  return typeof v === 'string' && (UI_ELEMENT_IDS as readonly string[]).includes(v) ? (v as UiElement) : null;
}

function wheelNotches(args: unknown): number | null {
  const w = objArg(args).wheel;
  if (typeof w !== 'object' || w === null) return null;
  const n = (w as Readonly<Record<string, unknown>>).notches;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

// ---------------------------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------------------------

function openModal(ctx: Ctx, modal: LinesModal): void {
  linesUi(ctx).set({ modal });
}

/** Opens the "line switcher" (also used by the status bar chip). */
export function openSwitcher(services: AppServices): void {
  linesUi(services).set({ modal: { kind: 'picker', mode: { kind: 'activate' } } });
}

function filterSetChecked(ctx: Ctx, set: FilterSet): boolean {
  const ids = targetLines(ctx);
  const cur = ctx.editor.getState().filters[set];
  return ids.length > 0 && ids.every((id) => cur.has(id));
}

/** Lines matching the panel's filter chips and search (all lines without filters). */
function filteredLines(ctx: Ctx): LineId[] {
  const store = ctx.store;
  if (store === null) return [];
  const table = buildLineTable(store.doc, store.idx);
  const ui = linesUi(ctx).get();
  const needNow = ui.filters.visibleNow || ui.filters.animatingNow;
  const hits = filterLines(table, ui.filters, needNow ? nowTracker(ctx).get() : null);
  return hits === null ? [...table.order] : table.order.filter((id) => hits.has(id));
}

function addPreset(
  id: string,
  preset: LinePreset,
  title: MessageKey,
  icon: string,
  level: 'guided' | 'full',
  order: number,
): CommandDef {
  return {
    id,
    title,
    category: 'Lines',
    icon,
    level,
    enabled: needProject,
    menus: [{ menu: 'lines.context', group: '0add', order }],
    run: (ctx) => {
      addLine(ctx, preset);
    },
  };
}

const UI_ELEMENT_ARG: ArgSpec = {
  kind: 'choice',
  label: { key: m.argElement },
  options: UI_ELEMENT_IDS.map((e) => ({ value: e, label: { key: m.uiElementName, params: { element: e } } })),
};

// ---------------------------------------------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------------------------------------------

export const LINE_COMMANDS: readonly CommandDef[] = [
  {
    id: 'lines.switcher',
    title: m.cmdSwitcher,
    category: 'Lines',
    icon: 'TextSearch',
    aliases: ['go to line', 'switch line', 'current line', 'LINE-01'],
    level: 'guided',
    hint: 40,
    enabled: needLines,
    run: (ctx) => {
      openSwitcher(ctx);
    },
  },
  {
    id: 'lines.showPanel',
    title: m.cmdShowPanel,
    category: 'Lines',
    icon: 'ListTree',
    aliases: ['line list', 'judge lines'],
    level: 'guided',
    enabled: needProject,
    run: (ctx) => {
      ctx.ui.showPanel(LINES_PANEL_ID);
    },
  },
  addPreset('lines.add', 'standard', m.cmdAddLine, 'Plus', 'guided', 0),
  addPreset('lines.addText', 'text', m.cmdAddText, TYPE_ICONS.text, 'guided', 1),
  {
    id: 'lines.addImage',
    title: m.cmdAddImage,
    category: 'Lines',
    icon: TYPE_ICONS.image,
    aliases: ['texture', 'picture'],
    level: 'guided',
    enabled: needProject,
    run: (ctx) => {
      openModal(ctx, { kind: 'image', gif: false });
    },
  },
  {
    id: 'lines.addUiMover',
    title: m.cmdAddUiMover,
    category: 'Lines',
    icon: TYPE_ICONS.uiMover,
    aliases: ['attachUI', 'HUD'],
    level: 'full',
    args: UI_ELEMENT_ARG,
    enabled: needProject,
    run: (ctx, args) => {
      const element = elementArg(args);
      if (element === null) openModal(ctx, { kind: 'uiElement' });
      else addLine(ctx, 'uiMover', { attachUI: element });
    },
  },
  {
    id: 'lines.addGif',
    title: m.cmdAddGif,
    category: 'Lines',
    icon: TYPE_ICONS.gif,
    level: 'full',
    enabled: needProject,
    run: (ctx) => {
      openModal(ctx, { kind: 'image', gif: true });
    },
  },
  addPreset('lines.addPaint', 'paint', m.cmdAddPaint, TYPE_ICONS.paint, 'full', 5),
  addPreset('lines.addHelper', 'helper', m.cmdAddHelper, TYPE_ICONS.helper, 'full', 6),
  {
    id: 'lines.addMany',
    title: m.cmdAddMany,
    category: 'Lines',
    icon: 'ListPlus',
    level: 'guided',
    enabled: needProject,
    run: (ctx) => {
      openModal(ctx, { kind: 'newLines', preset: 'standard' });
    },
  },
  {
    id: 'lines.addRpe24',
    title: m.cmdAddRpe24,
    category: 'Lines',
    icon: 'Rows4',
    aliases: ['RPE', '24'],
    level: 'full',
    enabled: needProject,
    run: (ctx) => {
      addRpeLines(ctx);
    },
  },
  {
    id: 'lines.duplicate',
    title: m.cmdDuplicate,
    category: 'Lines',
    icon: 'Copy',
    level: 'guided',
    enabled: needTargets,
    menus: [{ menu: 'lines.context', group: '1edit', order: 0 }],
    run: (ctx, args) => {
      duplicateLines(ctx, targetLines(ctx), {
        withNotes: boolArg(args, 'withNotes', true),
        withChildren: boolArg(args, 'withChildren', false),
        asChild: boolArg(args, 'asChild', false),
      });
    },
  },
  {
    id: 'lines.delete',
    title: m.cmdDelete,
    category: 'Lines',
    icon: 'Trash2',
    level: 'guided',
    enabled: needUnlockedTargets,
    menus: [{ menu: 'lines.context', group: '9delete', order: 0 }],
    run: async (ctx) => {
      await deleteFlow(ctx, targetLines(ctx));
    },
  },
  {
    id: 'lines.prev',
    title: m.cmdPrev,
    category: 'Lines',
    icon: 'ChevronUp',
    level: 'guided',
    hint: 20,
    repeat: true,
    enabled: needLines,
    run: (ctx) => {
      stepActiveLine(ctx, -1);
    },
  },
  {
    id: 'lines.next',
    title: m.cmdNext,
    category: 'Lines',
    icon: 'ChevronDown',
    level: 'guided',
    hint: 20,
    repeat: true,
    enabled: needLines,
    run: (ctx) => {
      stepActiveLine(ctx, 1);
    },
  },
  {
    id: 'lines.wheelSwitch',
    title: m.cmdWheelSwitch,
    category: 'Lines',
    icon: 'MousePointer2',
    aliases: ['Ctrl+wheel'],
    level: 'guided',
    enabled: needLines,
    run: (ctx, args) => {
      const notches = wheelNotches(args);
      if (notches === null) stepActiveLine(ctx, 1);
      else wheelSwitch(ctx, notches);
    },
  },
  {
    id: 'lines.toFront',
    title: m.cmdToFront,
    category: 'Lines',
    icon: 'BringToFront',
    aliases: ['zOrder'],
    level: 'guided',
    enabled: needUnlockedTargets,
    menus: [{ menu: 'lines.context', group: '3order', order: 0 }],
    run: (ctx) => {
      setZExtreme(ctx, targetLines(ctx), 'front');
    },
  },
  {
    id: 'lines.toBack',
    title: m.cmdToBack,
    category: 'Lines',
    icon: 'SendToBack',
    aliases: ['zOrder'],
    level: 'guided',
    enabled: needUnlockedTargets,
    menus: [{ menu: 'lines.context', group: '3order', order: 1 }],
    run: (ctx) => {
      setZExtreme(ctx, targetLines(ctx), 'back');
    },
  },
  {
    id: 'lines.moveUp',
    title: m.cmdMoveUp,
    category: 'Lines',
    icon: 'ArrowUp',
    aliases: ['export order', 'index'],
    level: 'full',
    enabled: needUnlockedTargets,
    run: (ctx) => {
      moveLinesBy(ctx, targetLines(ctx), -1);
    },
  },
  {
    id: 'lines.moveDown',
    title: m.cmdMoveDown,
    category: 'Lines',
    icon: 'ArrowDown',
    aliases: ['export order', 'index'],
    level: 'full',
    enabled: needUnlockedTargets,
    run: (ctx) => {
      moveLinesBy(ctx, targetLines(ctx), 1);
    },
  },
  {
    id: 'lines.rename',
    title: m.cmdRename,
    category: 'Lines',
    icon: 'Pencil',
    level: 'guided',
    enabled: needSingleTarget,
    run: (ctx) => {
      const [id] = targetLines(ctx);
      if (id === undefined) return;
      ctx.ui.showPanel(LINES_PANEL_ID);
      const ui = linesUi(ctx);
      ui.set({ renaming: id, reveal: { line: id, seq: (ui.get().reveal?.seq ?? 0) + 1 } });
    },
  },
  {
    id: 'lines.setParent',
    title: m.cmdSetParent,
    category: 'Lines',
    icon: 'Network',
    aliases: ['father', 'parent'],
    level: 'guided',
    enabled: needUnlockedTargets,
    menus: [{ menu: 'lines.context', group: '2structure', order: 0 }],
    run: (ctx) => {
      openModal(ctx, { kind: 'picker', mode: { kind: 'parent', lines: targetLines(ctx) } });
    },
  },
  {
    id: 'lines.detach',
    title: m.cmdDetach,
    category: 'Lines',
    icon: 'Unlink',
    level: 'guided',
    enabled: (ctx) => {
      const t = needUnlockedTargets(ctx);
      if (t !== true) return t;
      const store = ctx.store;
      const any = targetLines(ctx).some((id) => (store?.doc.lines.get(id)?.parent ?? null) !== null);
      return any ? true : { key: m.noParent };
    },
    menus: [{ menu: 'lines.context', group: '2structure', order: 1 }],
    run: async (ctx) => {
      const store = ctx.store;
      const ids = targetLines(ctx).filter((id) => (store?.doc.lines.get(id)?.parent ?? null) !== null);
      await setParentFlow(ctx, ids, null);
    },
  },
  {
    id: 'lines.changeType',
    title: m.cmdChangeType,
    category: 'Lines',
    icon: 'Shapes',
    aliases: ['line type', 'texture', 'attachUI'],
    level: 'guided',
    enabled: needSingleTarget,
    menus: [{ menu: 'lines.context', group: '2structure', order: 2 }],
    run: async (ctx) => {
      const [id] = targetLines(ctx);
      if (id !== undefined) await changeTypeFlow(ctx, id);
    },
  },
  {
    id: 'lines.toggleHidden',
    title: m.cmdToggleHidden,
    category: 'Lines',
    icon: 'EyeOff',
    aliases: ['eye', 'hide'],
    level: 'guided',
    enabled: needTargets,
    checked: (ctx) => filterSetChecked(ctx, 'hiddenLines'),
    menus: [{ menu: 'lines.context', group: '5view', order: 0 }],
    run: (ctx) => {
      toggleFilter(ctx, 'hiddenLines', targetLines(ctx));
    },
  },
  {
    id: 'lines.toggleSolo',
    title: m.cmdToggleSolo,
    category: 'Lines',
    icon: 'Focus',
    level: 'guided',
    enabled: needTargets,
    checked: (ctx) => filterSetChecked(ctx, 'soloLines'),
    menus: [{ menu: 'lines.context', group: '5view', order: 1 }],
    run: (ctx) => {
      toggleFilter(ctx, 'soloLines', targetLines(ctx));
    },
  },
  {
    id: 'lines.toggleLock',
    title: m.cmdToggleLock,
    category: 'Lines',
    icon: 'Lock',
    level: 'guided',
    enabled: needTargets,
    checked: (ctx) => filterSetChecked(ctx, 'lockedLines'),
    menus: [{ menu: 'lines.context', group: '5view', order: 2 }],
    run: (ctx) => {
      toggleFilter(ctx, 'lockedLines', targetLines(ctx));
    },
  },
  {
    id: 'lines.focusHitsounds',
    title: m.cmdFocusHitsounds,
    category: 'Lines',
    icon: 'Volume2',
    aliases: ['mute other lines', 'VIEW-15'],
    level: 'full',
    enabled: needTargets,
    checked: (ctx) => filterSetChecked(ctx, 'focusHitsoundLines'),
    menus: [{ menu: 'lines.context', group: '5view', order: 3 }],
    run: (ctx) => {
      toggleFilter(ctx, 'focusHitsoundLines', targetLines(ctx));
    },
  },
  {
    id: 'lines.showAll',
    title: m.cmdShowAll,
    category: 'Lines',
    icon: 'Eye',
    level: 'guided',
    enabled: (ctx) => {
      const f = ctx.editor.getState().filters;
      return previewDifferences(f) === null ? { key: m.nothingHidden } : true;
    },
    run: (ctx) => {
      showAll(ctx);
    },
  },
  {
    id: 'lines.unlockAll',
    title: m.cmdUnlockAll,
    category: 'Lines',
    icon: 'LockOpen',
    level: 'guided',
    enabled: (ctx) => (ctx.editor.getState().filters.lockedLines.size === 0 ? { key: m.nothingLocked } : true),
    run: (ctx) => {
      unlockAll(ctx);
    },
  },
  {
    id: 'lines.pinLane',
    title: m.cmdPinLane,
    category: 'Lines',
    icon: 'Pin',
    aliases: ['multi-line', 'extra lane'],
    level: 'full',
    enabled: needTargets,
    checked: (ctx) => {
      const ids = targetLines(ctx);
      const cur = ctx.editor.getState().pinnedLanes;
      return ids.length > 0 && ids.every((id) => cur.includes(id));
    },
    menus: [{ menu: 'lines.context', group: '6lane', order: 0 }],
    run: (ctx) => {
      togglePinnedLanes(ctx, targetLines(ctx));
    },
  },
  {
    id: 'lines.alsoShow',
    title: m.cmdAlsoShow,
    category: 'Lines',
    icon: 'Layers',
    aliases: ['ghost notes', 'VIEW-06'],
    level: 'guided',
    enabled: needTargets,
    checked: (ctx) => {
      const ids = targetLines(ctx);
      const cur = ctx.editor.getState().alsoShow;
      return ids.length > 0 && ids.every((id) => cur.includes(id));
    },
    menus: [{ menu: 'lines.context', group: '6lane', order: 1 }],
    run: (ctx) => {
      toggleAlsoShow(ctx, targetLines(ctx));
    },
  },
  {
    id: 'lines.alsoShowPick',
    title: m.cmdAlsoShowPick,
    category: 'Lines',
    icon: 'Layers',
    level: 'guided',
    enabled: needLines,
    run: (ctx) => {
      openModal(ctx, { kind: 'picker', mode: { kind: 'alsoShow' } });
    },
  },
  {
    id: 'lines.copy',
    title: m.cmdCopy,
    category: 'Lines',
    icon: 'ClipboardCopy',
    level: 'guided',
    enabled: needTargets,
    menus: [{ menu: 'lines.context', group: '1edit', order: 1 }],
    run: async (ctx) => {
      await copyLines(ctx, targetLines(ctx));
    },
  },
  {
    id: 'lines.cut',
    title: m.cmdCut,
    category: 'Lines',
    icon: 'Scissors',
    level: 'guided',
    enabled: needUnlockedTargets,
    menus: [{ menu: 'lines.context', group: '1edit', order: 2 }],
    run: async (ctx) => {
      await cutLines(ctx, targetLines(ctx));
    },
  },
  {
    id: 'lines.paste',
    title: m.cmdPaste,
    category: 'Lines',
    icon: 'ClipboardPaste',
    level: 'guided',
    enabled: needProject,
    menus: [{ menu: 'lines.context', group: '1edit', order: 3 }],
    run: async (ctx) => {
      await pasteLines(ctx);
    },
  },
  {
    id: 'lines.selectAll',
    title: m.cmdSelectAll,
    category: 'Lines',
    icon: 'ListChecks',
    level: 'guided',
    enabled: needLines,
    run: (ctx) => {
      const ids = filteredLines(ctx);
      const active = ctx.editor.getState().activeLine;
      selectLines(ctx, ids, active !== null && ids.includes(active) ? active : (ids[0] ?? null));
    },
  },
  {
    id: 'lines.groups',
    title: m.cmdGroups,
    category: 'Lines',
    icon: 'Tags',
    aliases: ['judgeLineGroup', 'group'],
    level: 'guided',
    enabled: needProject,
    run: (ctx) => {
      openModal(ctx, { kind: 'groups' });
    },
  },
  {
    id: 'lines.setGroup',
    title: m.cmdSetGroup,
    category: 'Lines',
    icon: 'Tag',
    level: 'guided',
    enabled: needUnlockedTargets,
    run: (ctx, args) => {
      const g = objArg(args).group;
      const store = ctx.store;
      if (g === null || g === undefined) {
        setGroup(ctx, targetLines(ctx), null);
        return;
      }
      if (typeof g !== 'number' || store === null || !store.doc.groups.has(g as GroupId)) {
        openModal(ctx, { kind: 'groups' });
        return;
      }
      setGroup(ctx, targetLines(ctx), g as GroupId);
    },
  },
  {
    id: 'lines.newGroup',
    title: m.cmdNewGroup,
    category: 'Lines',
    icon: 'FolderPlus',
    level: 'guided',
    args: { kind: 'text', label: { key: m.argGroupName } },
    enabled: needProject,
    run: (ctx, args) => {
      const name = typeof args === 'string' ? args : objArg(args).name;
      if (typeof name !== 'string' || name.trim() === '') {
        openModal(ctx, { kind: 'groups' });
        return;
      }
      const ids = ctx.editor.getState().selection.lines.size > 0 ? targetLines(ctx) : [];
      createGroup(ctx, name, ids);
    },
  },
  {
    id: 'lines.toggleView',
    title: m.cmdToggleView,
    category: 'Lines',
    icon: 'ListTree',
    level: 'guided',
    checked: (ctx) => viewOf(ctx) === 'tree',
    run: (ctx) => {
      ctx.settings.setExt(VIEW_SETTING, viewOf(ctx) === 'tree' ? 'order' : 'tree');
    },
  },
  {
    id: 'lines.clearFilters',
    title: m.cmdClearFilters,
    category: 'Lines',
    icon: 'FilterX',
    level: 'guided',
    enabled: (ctx) => (filtersActive(linesUi(ctx).get().filters) ? true : { key: m.noFiltersOn }),
    run: (ctx) => {
      linesUi(ctx).set({ filters: NO_FILTERS });
    },
  },
  {
    id: 'lines.expandAll',
    title: m.cmdExpandAll,
    category: 'Lines',
    icon: 'ChevronsUpDown',
    level: 'guided',
    enabled: needLines,
    run: (ctx) => {
      linesUi(ctx).set({ collapsed: new Set() });
    },
  },
  {
    id: 'lines.collapseAll',
    title: m.cmdCollapseAll,
    category: 'Lines',
    icon: 'ChevronsDownUp',
    level: 'guided',
    enabled: needLines,
    run: (ctx) => {
      const store = ctx.store;
      if (store === null) return;
      const parents = new Set<LineId>();
      for (const id of store.doc.lineOrder) if (store.idx.childrenOf(id).length > 0) parents.add(id);
      linesUi(ctx).set({ collapsed: parents });
    },
  },
];

// ---------------------------------------------------------------------------------------------------------------
// Key bindings
// ---------------------------------------------------------------------------------------------------------------

/** Lines panel has focus and no text field does. */
const IN_PANEL = 'focus.lines && !textInput';
/** ... with lines selected (acting on them). */
const ON_SELECTION = 'focus.lines && sel.lines && !textInput';
/**
 * Delete in the Lines panel. `!gesture` (never delete lines mid-drag) and `!dialog` (dialogs take their own keys
 * anyway) make it more specific than the other Delete bindings that also hold while the Lines panel has focus
 * (selected segments: specificity 5, selected markers: 6). Equal specificity there was a real (red) conflict in both
 * presets (UX-061); now the focused panel decides.
 */
const DELETE_WHEN = `${ON_SELECTION} && !gesture && !dialog`;

const kb = (chord: string, command: string, extra: Partial<KeyBinding> = {}): KeyBinding => ({
  chord,
  command,
  ...extra,
});

const DEFAULT_BINDINGS: readonly KeyBinding[] = [
  kb('KeyL', 'lines.switcher'),
  kb('Shift+KeyL', 'lines.add'),
  kb('Ctrl+ArrowUp', 'lines.prev'),
  kb('Ctrl+ArrowDown', 'lines.next'),
  // Cmd+] / Cmd+[ are browser Back / Forward on macOS: menu, palette and panel buttons remain.
  kb('Ctrl+BracketRight', 'lines.toFront', { mac: null }),
  kb('Ctrl+BracketLeft', 'lines.toBack', { mac: null }),
  kb('Delete', 'lines.delete', { when: DELETE_WHEN }),
  kb('Backspace', 'lines.delete', { when: DELETE_WHEN }),
  kb('Ctrl+KeyC', 'lines.copy', { when: ON_SELECTION }),
  kb('Ctrl+KeyX', 'lines.cut', { when: ON_SELECTION }),
  kb('Ctrl+KeyV', 'lines.paste', { when: IN_PANEL }),
  kb('Ctrl+KeyD', 'lines.duplicate', { when: ON_SELECTION }),
  kb('Ctrl+KeyA', 'lines.selectAll', { when: IN_PANEL }),
  kb('F2', 'lines.rename', { when: ON_SELECTION }),
  kb('KeyH', 'lines.toggleHidden', { when: ON_SELECTION }),
  kb('Alt+Shift+ArrowUp', 'lines.moveUp', { when: ON_SELECTION, altFree: null }),
  kb('Alt+Shift+ArrowDown', 'lines.moveDown', { when: ON_SELECTION, altFree: null }),
];

const RPE_BINDINGS: readonly KeyBinding[] = [
  ...DEFAULT_BINDINGS,
  // LINE-01: Ctrl + wheel switches the line when nothing is selected (selected keys / notes: fine-tune, W2-03/04a).
  kb('Ctrl+Wheel', 'lines.wheelSwitch', { when: '!sel.notes && !sel.keys && !sel.segments' }),
  // VIEW-07 (RPE Tab: the preview's line info readout) is bound by the command's owner, W2-02
  // (`preview.toggleReadout`, contracted in app/registry/commands.ts).
];

export const LINE_KEYBINDINGS: Readonly<Record<KeymapPresetId, readonly KeyBinding[]>> = {
  default: DEFAULT_BINDINGS,
  rpe: RPE_BINDINGS,
};

// ---------------------------------------------------------------------------------------------------------------
// Palette `#` provider
// ---------------------------------------------------------------------------------------------------------------

/** Most results the `#` provider lists. */
export const PALETTE_LIMIT = 50;

function paletteItems(services: AppServices, table: LineTable, query: string): PaletteItem[] {
  return matchLines(table, query, PALETTE_LIMIT).map(({ info }) => {
    const group = info.groupName ?? '';
    return {
      id: `line:${String(info.id)}`,
      title: { key: m.paletteTitle, params: { index: info.index, name: info.name } },
      detail: {
        key: m.paletteDetail,
        params: { type: info.type, notes: info.notes, group, hasGroup: group === '' ? 'no' : 'yes' },
      },
      icon: info.uiElement !== null ? UI_ELEMENT_ICONS[info.uiElement] : TYPE_ICONS[info.type],
      run: () => {
        activateLine(services, info.id, { select: true, reveal: true });
      },
    };
  });
}

/** `#12`, `#main`: jump to a line (Enter makes it active and selects it). */
export function linesPaletteProvider(services: AppServices): PaletteProvider {
  return {
    prefix: '#',
    search: (query) => {
      const store = services.store;
      if (store === null) return [];
      return paletteItems(services, buildLineTable(store.doc, store.idx), query.replace(/^#/, ''));
    },
  };
}
